import { DELIMITERS } from '@/main/app.constants';
import {
  APPEND,
  COLUMN_INDEX,
  COLUMN_NAME,
  DATAFILE_ITEM_TYPES,
  DAY_MONTH_FIRST_OPTIONS,
  EMPTY_ROW_IDENTIFIER,
  FILE_OUTPUT_TYPES,
  INTERPOLATION_METHODS,
  SIGNAL_VALUE_COLUMN_OPTIONS,
  VALIDATION_MODES,
} from '@/tools/importDatafile/importDatafile.constants';
import { FieldDelimiterEnum, ItemTypeEnum, ValidationModeEnum } from '@seeqdev/api/model/DatafileInputV1';
import { splitDuration } from '@seeqdev/utils';
import type { FormElement } from '@/formbuilder/formBuilder.constants';
import type { ImportDatafileState, ImportDatafileTimezone } from '@/tools/importDatafile/importDatafile.store';
import type { DatafileInputV1 } from '@seeqdev/api/model/DatafileInputV1';
import type { DatafileOutputV1 } from '@seeqdev/api/model/DatafileOutputV1';

export function createRehydratePayload(
  data: DatafileOutputV1,
  timezones: ImportDatafileTimezone[],
): Partial<ImportDatafileState> {
  const parseDuration = (duration: string | undefined) =>
    (duration === undefined ? undefined : splitDuration(duration)) ?? { value: NaN, units: undefined };

  const valueColumnNames = data.valueColumnNames;
  const valueColumnIndices = data.valueColumnIndices;

  const valueColumnOption =
    valueColumnNames !== '' ? COLUMN_NAME : valueColumnIndices !== '' ? COLUMN_INDEX : SIGNAL_VALUE_COLUMN_OPTIONS[0];

  return {
    filename: data.filename,
    description: data.description,
    endColumnIndex: data.endColumnIndex,
    endColumnName: data.endColumnName,
    fieldDelimiter: DELIMITERS.find((item) => item.value === data.fieldDelimiter),
    fileOutputType: data.append === false ? FILE_OUTPUT_TYPES[0] : FILE_OUTPUT_TYPES[1],
    firstDataRow: data.firstDataRow,
    interpolationMethod: INTERPOLATION_METHODS.find((item) => item.value === data.interpolationMethod),
    interpolationMethodRow:
      data.interpolationMethodRow === EMPTY_ROW_IDENTIFIER ? undefined : data.interpolationMethodRow,
    keyColumnIndex: data.keyColumnIndex,
    keyColumnName: data.keyColumnName,
    maxDuration: parseDuration(data.maximumDuration),
    maxInterpolation: parseDuration(data.maximumInterpolation),
    maxInterpolationRow:
      data.maximumInterpolationRow === EMPTY_ROW_IDENTIFIER ? undefined : data.maximumInterpolationRow,
    namePrefix: data.namePrefix,
    nameSuffix: data.nameSuffix,
    nameRow: data.nameRow,
    timezone: timezones.find((item) => item.displayName === data.timeZone),
    valueUomRow: data.valueUomRow === EMPTY_ROW_IDENTIFIER ? undefined : data.valueUomRow,
    validationMode: VALIDATION_MODES.find((item) => item.value === data.validationMode),
    lenientDaylightSavings: data.lenientDaylightSavings,
    descriptionRow: data.descriptionRow === EMPTY_ROW_IDENTIFIER ? undefined : data.descriptionRow,
    valueColumnIndices: valueColumnNames !== '' || valueColumnIndices === '' ? undefined : valueColumnIndices,
    valueColumnNames: valueColumnNames !== '' ? valueColumnNames : undefined,
    valueColumnOption,
    valueUom: data.valueUom,
    itemType: DATAFILE_ITEM_TYPES.find((item) => item.value === data.itemType),
    conditionName: data.conditionName,
    dayFirstDefault: DAY_MONTH_FIRST_OPTIONS.find((item) => item.value === data.dayFirstDefault),
  };
}

export type ImportDatafileApiState = Pick<
  ImportDatafileState,
  | 'uploadFilename'
  | 'filename'
  | 'description'
  | 'name'
  | 'fieldDelimiter'
  | 'fileOutputType'
  | 'firstDataRow'
  | 'interpolationMethod'
  | 'interpolationMethodRow'
  | 'keyColumnIndex'
  | 'keyColumnName'
  | 'maxInterpolation'
  | 'maxInterpolationRow'
  | 'namePrefix'
  | 'nameSuffix'
  | 'nameRow'
  | 'timezone'
  | 'valueUomRow'
  | 'validationMode'
  | 'lenientDaylightSavings'
  | 'descriptionRow'
  | 'valueColumnIndices'
  | 'valueColumnNames'
  | 'valueColumnOption'
  | 'valueUom'
  | 'endColumnIndex'
  | 'endColumnName'
  | 'maxDuration'
  | 'itemType'
  | 'conditionName'
  | 'dayFirstDefault'
>;

export function getImportDatafileApiObject(state: ImportDatafileApiState): DatafileInputV1 {
  if (!state.dayFirstDefault) {
    throw new Error('Import datafile date-order defaults are unavailable. Reload after system configuration loads.');
  }

  const { itemType, valueColumnOption, valueColumnNames } = state;
  const valueColumnIndices =
    itemType.value === ItemTypeEnum.Condition && valueColumnOption.value === 'None'
      ? 0
      : valueColumnOption.value === COLUMN_INDEX.value
        ? state.valueColumnIndices
        : undefined;

  const getValueColumnNames = () => {
    if (valueColumnOption.value !== COLUMN_NAME.value) return undefined;
    return Array.isArray(valueColumnNames) ? valueColumnNames.join(',') : valueColumnNames;
  };

  return {
    uploadFilename: state.uploadFilename!,
    filename: state.filename,
    description: state.description,
    name: state.name!,
    fieldDelimiter: state.fieldDelimiter?.value ?? FieldDelimiterEnum.Comma,
    append: state.fileOutputType?.value === APPEND,
    firstDataRow: state.firstDataRow,
    interpolationMethod: state.interpolationMethod?.value ?? '',
    interpolationMethodRow: state.interpolationMethodRow || EMPTY_ROW_IDENTIFIER,
    keyColumnIndex: state.keyColumnIndex,
    keyColumnName: state.keyColumnName,
    maximumInterpolation: `${state.maxInterpolation.value}${state.maxInterpolation.units}`,
    maximumInterpolationRow: state.maxInterpolationRow || EMPTY_ROW_IDENTIFIER,
    namePrefix: state.namePrefix,
    nameSuffix: state.nameSuffix,
    nameRow: state.nameRow,
    timeZone: state.timezone?.displayName,
    valueUomRow: state.valueUomRow || EMPTY_ROW_IDENTIFIER,
    validationMode: state.validationMode?.value ?? ValidationModeEnum.Fail,
    lenientDaylightSavings: state.lenientDaylightSavings,
    descriptionRow: state.descriptionRow || EMPTY_ROW_IDENTIFIER,
    valueColumnIndices: valueColumnIndices as DatafileInputV1['valueColumnIndices'],
    valueColumnNames: getValueColumnNames(),
    valueUom: state.valueUom,
    endColumnIndex: state.endColumnIndex,
    endColumnName: state.endColumnName,
    maximumDuration: `${state.maxDuration.value}${state.maxDuration.units}`,
    itemType: itemType.value,
    conditionName: itemType.value === ItemTypeEnum.Condition ? state.conditionName : undefined,
    dayFirstDefault: state.dayFirstDefault.value,
  };
}

export const getLabelFromItemType = (isSignalSelected: boolean, translationKey: string): string => {
  if (isSignalSelected) {
    return `IMPORTS.${translationKey}.SIGNAL`;
  } else {
    return `IMPORTS.${translationKey}.CONDITION`;
  }
};

export const getTooltipFromItemType = (isSignalSelected: boolean, translationKey: string): string => {
  if (isSignalSelected) {
    return `IMPORTS.TOOLTIPS.${translationKey}.SIGNAL`;
  } else {
    return `IMPORTS.TOOLTIPS.${translationKey}.CONDITION`;
  }
};

export const makeIndexInput = (
  baseName: string,
  label: string,
  tooltip: string,
  value: number,
  setter: (value: number) => void,
  displayNumber: boolean,
  includeIf: boolean,
  required: boolean,
  disabled: boolean,
) =>
  ({
    component: 'TextFieldFormComponent',
    name: `${baseName}Input`,
    testId: baseName,
    value,
    label,
    tooltip,
    displayNumber,
    includeIf,
    onChange: setter,
    wrapperClassNames: 'w-100',
    size: 'sm',
    type: 'number',
    disabled,
    validation: (value) => (required ? true : value) && (Number(value) || 0) < 0,
  }) as FormElement;

export const makeTextInput = <T extends string | number>(
  baseName: string,
  label: string,
  tooltip: string | undefined,
  value: T,
  setter: (value: T) => void,
  displayNumber: boolean,
  includeIf: boolean,
  required: boolean,
  validation: ((value: string | number) => boolean | undefined) | undefined,
  disabled: boolean,
) =>
  ({
    component: 'TextFieldFormComponent',
    name: `${baseName}Input`,
    testId: baseName,
    value,
    label,
    tooltip,
    displayNumber,
    includeIf,
    onChange: setter as (value: string | number) => void,
    wrapperClassNames: 'w-100',
    size: 'sm',
    disabled,
    validation: validation ?? (() => (required ? undefined : false)),
  }) as FormElement;
